import { gql } from '@apollo/client'
import { GENERATION_BASIS_VIEW_FIELDS } from '@/graphql/generation-basis-queries'

export const GET_ADS_MANAGER_DATA = gql`
    query GetAdsManagerData($dateRange: DateRangeInput, $campaignId: String) {
        AdCampaign {
            adsManagerData(dateRange: $dateRange, campaignId: $campaignId) {
                __typename
                ... on AdsManagerCampaign {
                    id
                    name
                    order
                    status
                    effective_status
                    objective
                    location_targeting
                    type
                    start_time
                    error
                    budget_mode
                    campaign_daily_budget
                    advantage_leads {
                        on
                        advantage_state
                        advantage_budget_state
                        advantage_audience_state
                        advantage_placement_state
                        label
                        checked_at
                        note
                    }
                    warning
                    audience_test {
                        rows {
                            ad_set_id
                            db_id
                            name
                            audience_hypothesis {
                                id
                                kind
                                label
                                stale_note
                                targeting_option_note
                                basis {
                                    ${GENERATION_BASIS_VIEW_FIELDS}
                                }
                            }
                            audience_mode
                            learning_status
                            spend
                            impressions
                            ctr
                            instant_form_leads
                            website_leads
                            leads
                            cpl
                            low_volume
                        }
                        totals {
                            spend
                            impressions
                            ctr
                            instant_form_leads
                            website_leads
                            leads
                            cpl
                        }
                        target_cpl
                        low_volume_min_leads
                        low_volume_min_target_cpls
                    }
                    insights {
                        impressions
                        reach
                        spend
                        purchase_roas
                        ctr
                        unique_ctr
                        cpm
                        actions {
                            page_engagement
                            purchase
                            landing_page_view
                            add_to_cart
                            post_engagement
                            view_content
                            video_view
                            post_reaction
                            initiate_checkout
                            link_click
                            like
                            post
                            comment
                            lead
                        }
                        action_values {
                            page_engagement
                            purchase
                            landing_page_view
                            add_to_cart
                            post_engagement
                            view_content
                            video_view
                            post_reaction
                            initiate_checkout
                            link_click
                            like
                            post
                            comment
                            lead
                        }
                    }
                    latest_actions {
                        event_type
                        origin
                        event_time
                        actor_name
                        object_name
                        extra_data
                    }
                    adSets {
                        id
                        name
                        status
                        effective_status
                        daily_budget
                        uses_campaign_budget
                        stored_budget
                        budget_move_note
                        min_spend_target
                        spend_cap
                        learning_stage_info
                        lookalike_note
                        optimization_goal
                        warm_audience_note
                        sales_funnel_stage
                        exclusion_note
                        db_id
                        audience_hypothesis {
                            id
                            kind
                            label
                            stale_note
                            targeting_option_note
                            basis {
                                ${GENERATION_BASIS_VIEW_FIELDS}
                            }
                        }
                        audience_mode
                        insights {
                            impressions
                            spend
                            purchase_roas
                            ctr
                            cpm
                            actions {
                                page_engagement
                                purchase
                                landing_page_view
                                add_to_cart
                                post_engagement
                                view_content
                                video_view
                                post_reaction
                                initiate_checkout
                                link_click
                                like
                                post
                                comment
                                lead
                            }
                            action_values {
                                page_engagement
                                purchase
                                landing_page_view
                                add_to_cart
                                post_engagement
                                view_content
                                video_view
                                post_reaction
                                initiate_checkout
                                link_click
                                like
                                post
                                comment
                                lead
                            }
                        }
                        latest_actions {
                            event_type
                            origin
                            event_time
                            actor_name
                            object_name
                            extra_data
                        }
                        ads {
                            id
                            db_id
                            ad_creative_id
                            name
                            status
                            effective_status
                            type
                            insights {
                                impressions
                                spend
                                ctr
                                cpm
                                actions {
                                    page_engagement
                                    purchase
                                    landing_page_view
                                    add_to_cart
                                    post_engagement
                                    view_content
                                    video_view
                                    post_reaction
                                    initiate_checkout
                                    link_click
                                    like
                                    post
                                    comment
                                    lead
                                }
                                action_values {
                                    page_engagement
                                    purchase
                                    landing_page_view
                                    add_to_cart
                                    post_engagement
                                    view_content
                                    video_view
                                    post_reaction
                                    initiate_checkout
                                    link_click
                                    like
                                    post
                                    comment
                                    lead
                                }
                            }
                            latest_actions {
                                event_type
                                origin
                                event_time
                                actor_name
                                object_name
                                extra_data
                            }
                        }
                    }
                }
                ... on AdsManagerError {
                    error
                    publisherId
                    timestamp
                }
            }
        }
    }
`

export const UPDATE_AD_SET_BUDGET = gql`
    mutation UpdateAdSetBudget($adSetId: String!, $budget: Float!, $expectedBudget: Float) {
        updateAdSetBudget(adSetId: $adSetId, budget: $budget, expectedBudget: $expectedBudget)
    }
`

export const TOGGLE_CAMPAIGN_STATUS = gql`
    mutation ToggleCampaignStatus($campaignId: String!) {
        toggleCampaignStatus(campaignId: $campaignId)
    }
`

export const TOGGLE_AD_SET_STATUS = gql`
    mutation ToggleAdSetStatus($adSetId: String!) {
        toggleAdSetStatus(adSetId: $adSetId)
    }
`

export const TOGGLE_AD_STATUS = gql`
    mutation ToggleAdStatus($adId: String!) {
        toggleAdStatus(adId: $adId)
    }
`

export const DELETE_CAMPAIGN = gql`
    mutation DeleteCampaign($campaignId: ID!) {
        deleteCampaign(campaignId: $campaignId)
    }
`

export const DELETE_ADSET = gql`
    mutation DeleteAdSet($adSetId: ID!) {
        deleteAdSet(adSetId: $adSetId)
    }
`

export const DELETE_AD = gql`
    mutation DeleteAd($adId: ID!) {
        deleteAd(adId: $adId)
    }
`

export const GET_CAMPAIGNS_WITH_ADSETS = gql`
    query GetCampaignsWithAdSets($campaignId: String) {
        AdCampaign {
            campaignsWithAdSets(campaignId: $campaignId) {
                id
                name
                campaign_id
                publisher_id
                external_id
                state
                objective
                adSetsByStage {
                    stage
                    adSets {
                        id
                        db_id
                        name
                        external_id
                        sales_funnel_stage
                        budget
                        status
                        audience_id
                        has_instant_forms
                        audience {
                            id
                            name
                            icon
                        }
                    }
                }
            }
        }
    }
`

export const ADD_CREATIVES_TO_CAMPAIGN = gql`
    mutation AddCreativesToCampaign($data: AddCreativesToCampaignInput!, $publisherId: String!) {
        addCreativesToCampaign(data: $data, publisherId: $publisherId) {
            success
            adsCreated
            message
        }
    }
`

export const AD_CAMPAIGN_STATUS_UPDATE = gql`
    subscription AdCampaignStatusUpdate($userId: ID!) {
        adCampaignStatusUpdate(userId: $userId) {
            id
            name
            status
            external_id
            error
            published_at
        }
    }
`

export const GET_AD_CAMPAIGN_FOR_EDIT = gql`
    query GetAdCampaignForEdit($adCampaignId: String!) {
        AdCampaign {
            adCampaignForEdit(adCampaignId: $adCampaignId) {
                id
                name
                objective
                location_targeting
                start_time
                budget_mode
                daily_budget
                ad_sets {
                    id
                    name
                    external_id
                    audience_id
                    sales_funnel_stage
                    budget
                    settings
                    ad_creative_ids
                    audience_hypothesis_id
                    audience_mode
                    account_audience_id
                    account_audience_name
                    account_audience_subtype
                    min_spend_target
                    spend_cap
                }
            }
        }
    }
`

export const UPDATE_AD_CAMPAIGN = gql`
    mutation UpdateAdCampaign($adCampaignId: ID!, $data: UpdateAdCampaignInput!) {
        updateAdCampaign(adCampaignId: $adCampaignId, data: $data)
    }
`

export const UPDATE_AD_CAMPAIGN_ORDER = gql`
    mutation UpdateAdCampaignOrder($updates: [AdCampaignOrderInput!]!) {
        updateAdCampaignOrder(updates: $updates) {
            id
            order
        }
    }
`
